import { yupResolver } from '~/helpers/yupResolver';
import classNames from 'classnames';
import { formatISO, parse } from 'date-fns';
import { EditorState, convertToRaw, type RawDraftContentState } from 'draft-js';
import { nanoid } from 'nanoid';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'urql';
import { array, number, object, string } from 'yup';
import { useAllAssets } from '~/components/AssetSelect/AssetSelect';
import { FindAddContact } from '~/components/FindAddContact';
import { FindAddSite, Site } from '~/components/FindAddSite';
import { useSites } from '~/components/FindAddSite/FindAddSite';
import { getAttributeFilterOptions } from '~/components/FindAddSite/SiteSelect';
import { FindAddTemplate } from '~/components/FindAddTemplate';
import { Prompt } from '~/components/Prompt';
import { SelectCombolist } from '~/components/SelectCombolist';
import { TagCombobox, TagEntityType } from '~/components/TagCombobox';
import { useTags } from '~/components/TagCombobox/tags';
import {
  AttributeSelect,
  AttributeSelect_Query,
} from '~/components/form/AttributeSelect';
import { ControlledStatusToggle } from '~/components/form/StatusToggle';
import { Button } from '~/components/form/SubmitButton';
import { Input } from '~/components/form/TextField';
import { TimePicker } from '~/components/form/TimePicker';
import { RadioGroup } from '~/components/form/checkradio';
import { Select } from '~/components/form/downshift/Select';
import { Editor } from '~/components/form/editor/Editor';
import {
  deserializeNotes,
  toInput,
} from '~/components/job-template/JobTemplateForm';
import {
  AssetDrivenJobPreview,
  generateTasksFromAssetDrivenTemplate,
} from '~/components/job/AssetDrivenJobPreview';
import { getTemplateBehavior } from '~/components/job/AssetDrivenTemplateIndicator';
import {
  ManageTasks,
  taskTemplateToTaskInput,
} from '~/components/job/CreateJobForm/ManageTasks';
import { CustomTaskForm } from '~/components/job/CustomTaskForm';
import {
  extractSpecificAssetIds,
  formatMonthlyPatternValue,
  normalizeByDay,
  normalizeFreq,
  parseMonthlyRepeatToUiSelector,
} from '~/components/automation/automationRepeat';
import { Avatar } from '~/components/ui/Avatar';
import { Dialog } from '~/components/ui/Dialog';
import { FacetedFilterSelect } from '~/components/ui/FacetedFilterSelect';
import { DAYS, getWeekNumber } from '~/components/ui/JobsRepeat';
import { Label } from '~/components/ui/Label';
import { SegmentedControl } from '~/components/ui/SegmentedControl';
import { getFragmentData, graphql } from '~/gql';
import {
  AutomationFormQuery,
  AutomationWhereType,
  JobTemplateFieldsFragment,
  Status,
  TaskInput,
} from '~/gql/graphql';
import { ContactFields } from '~/graphql/fragment/ContactFields';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { isTemplateModified } from '~/helpers/automationTemplateHelpers';
import { getOrdinalSuffix } from '~/helpers/ordinalSuffix';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { SideLayout } from '~/layouts/side/SideLayout';
import { Accordion } from '../../ui/Accordion';

const safelyDeserializeTemplateNotes = (
  notes: string | null | undefined,
  templateId?: string
): RawDraftContentState | null => {
  if (!notes) {
    return null;
  }

  try {
    return deserializeNotes(notes);
  } catch (error) {
    console.error('Failed to deserialize template notes', {
      error,
      templateId,
    });
    return null;
  }
};

// Display order: Monday through Sunday (M T W T F S S)
const DAYS_DISPLAY_ORDER: string[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const CreateJobTemplateDocument = graphql(`
  mutation CreateJobTemplate($input: JobTemplateInput!) {
    createJobTemplate(input: $input) {
      id
    }
  }
`);

export type FormValues = {
  status: Status;
  name: string;
  // TODO BACKEND: Ensure 'on' field accepts all these string values in AutomationInput
  on: string; //  "repeat"  | "booking-received" | "booking-changed" | "booking-cancelled"
  whereType: AutomationWhereType;
  matchAttribute: { key: string; value: string[] }[]; // whereType === 'match'
  sites: Site[]; // whereType === 'select'
  // TODO BACKEND: includeFuturePlaces requires backend logic to auto-add new sites to automations
  // includeFuturePlaces: boolean;
  jobName: string;
  template: string;
  owner: string[] | null;
  assignee: string[] | null;
  // TODO BACKEND: day field should be replaced with new schedule fields in AutomationInput
  day: number;
  // TODO BACKEND: time field should be replaced with new schedule fields in AutomationInput
  time: string | null;
  runOnExistingBookings: string;
  repeatStartDate: string | null;
  repeat: {
    freq: string;
    interval: string;
    byday?: string[] | null;
    bymonthday?: string | null; // TODO BACKEND: Should be single value, not array
  };
  maintainRepeats: number | null;
  // Flag indicating whether the template (templateData) has been modified
  // compared to the original baseline template. This is computed in AutomationForm
  // using automationTemplateHelpers.isTemplateModified and passed up to routes
  // so they don't need to re-run comparison logic.
  templateModified?: boolean;
  // Template editing data (only used for system-generated templates)
  templateData?: {
    name: string;
    notes: RawDraftContentState | null;
    tasks: TaskInput[];
    tags: string[];
    includeAttributes: string[] | null;
    jobAttributeIds: string[] | null;
    attachments: Array<{
      id: string;
      kind: string;
      ext: string;
      thumb: string;
      url: string;
      originalFilename: string;
    }>;
  };
  // Asset-driven template fields
  selectedAssetIds?: string[];
  // TODO BACKEND: avoidBookingOverlap requires backend scheduling logic to check for conflicts
  // avoidBookingOverlap: boolean;
};

type Props = {
  data: AutomationFormQuery;
  onSubmit: (data: FormValues) => void;
  onCreateTemplate?: (
    getFormValues: () => FormValues,
    openCreateDialog: () => void
  ) => void;
  externalShowCreateDialog?: boolean;
  onCreateDialogShown?: () => void;
  onTemplateCreated?: (newTemplateId: string, formData: FormValues) => void;
  disablePrompt?: boolean;
};

// Type extension for Automation with future fields not yet in GraphQL schema
// TODO BACKEND: Remove this type extension once these fields are added to the backend schema
type AutomationWithFutureFields = NonNullable<
  AutomationFormQuery['automation']
> & {
  includeFuturePlaces?: boolean;
  excludeDays?: boolean;
  excludeDaysList?: string[];
  excludeFallback?: 'before' | 'after';
  excludeTime?: string | null;
  avoidBookingOverlap?: boolean;
};

const schema = object({
  name: string().required('Required'),
  on: string().oneOf(['repeat', 'check-in', 'check-out']).required('Required'),
  sites: array().required('Required'),
  jobName: string().required('Required'),
  template: string().required('Required'),
  owner: array().of(string()).nullable(),
  assignee: array().of(string()).nullable(),
  day: number().required('Required'),
  time: string().nullable(),
  repeat: object({
    freq: string(),
    interval: number()
      .integer('Must be a whole number')
      .min(1, 'Must be at least 1')
      .max(999, 'Must be at most 999')
      .required('Required'),
    byday: array().of(string()).nullable(),
    bymonthday: string().nullable(),
  }),
});

const dayOptions = [...Array(29)].map((_, i) => {
  const value = (i - 14).toString();
  return {
    value,
    label:
      value === '0'
        ? 'Same day as'
        : i - 14 < 0
        ? `${((i - 14) * -1).toString()} days before`
        : `${value} days after`,
  };
});

/**
 * AutomationForm Component
 *
 * BACKEND COORDINATION REQUIRED:
 *
 * 1. Data Format Issues (LEGACY): Backend historically sent numeric codes for freq ('1', '2', '3')
 *    and byday ('0'-'6'). Frontend now expects string values ('daily', 'weekly', 'monthly' and day names).
 *    See normalizeFreq() and normalizeByDay() functions for defensive backward-compatibility handling.
 *    Note: Frontend always sends correct 2-letter lowercase codes ('mo','tu', etc.) which backend
 *    uppercases to the format required by Weekday.fromStr (SU, MO, TU, WE, TH, FR, SA).
 *
 * 2. Schema Issues:
 *    - bymonthday: Backend returns array but frontend expects single value
 *
 * 3. Schedule Fields Mapping (WORKING for days before/after):
 *    Backend: Uses signed integer `day` field (negative=before, positive=after, 0=same day)
 *    Frontend: Separate scheduleQuantity/scheduleUnit/schedulePosition fields
 *    Current Support: ✅ Days before/after works perfectly
 *    Missing: Backend doesn't support 'hour' or 'week' units (only 'day')
 *    NOTE: User can change hour/week in UI, but values will always save as days
 *
 * 4. New Features requiring backend support (currently commented out in UI):
 *    - excludeDays: Skip scheduling on specified days of the week
 *    - includeFuturePlaces: Auto-include future sites when all sites selected
 *    - avoidBookingOverlap: Prevent scheduling during active bookings
 */
export function AutomationForm({
  data,
  onSubmit,
  onCreateTemplate,
  externalShowCreateDialog,
  onCreateDialogShown,
  onTemplateCreated,
  disablePrompt,
}: Props) {
  const navigation = useNavigation();
  // Cast automation to include future fields for type safety
  const automation = data.automation as
    | AutomationWithFutureFields
    | null
    | undefined;
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  // Get full template data from fragments FIRST (before defaultValues needs it)
  const templates = useMemo<JobTemplateFieldsFragment[]>(() => {
    const templatesFromList = data.jobTemplates.map(
      (template) =>
        getFragmentData(
          JobTemplateFields,
          template
        ) as JobTemplateFieldsFragment
    );

    return templatesFromList;
  }, [data.jobTemplates]);

  const mergedAutomationTemplate =
    useMemo<JobTemplateFieldsFragment | null>(() => {
      if (!automation?.template) {
        return null;
      }

      const automationTemplate =
        automation.template as unknown as JobTemplateFieldsFragment;

      const fromList = templates.find((t) => t.id === automation.template.id);
      if (!fromList) {
        console.warn(
          '[AutomationForm] Template not found in jobTemplates list, using automation.template'
        );
        return automationTemplate;
      }

      // CRITICAL: Only merge if fromList actually has the includeAttributes/jobAttributeIds fields
      // Until codegen runs with the updated fragment, fromList will have undefined for these fields
      // and merging would overwrite the correct values from automation.template
      const hasAttributeFields =
        'includeAttributes' in fromList || 'jobAttributeIds' in fromList;

      if (!hasAttributeFields) {
        console.warn(
          '[AutomationForm] ⚠️  JobTemplateFields fragment missing attribute fields - using automation.template directly'
        );
        return automationTemplate;
      }

      // CRITICAL: The nested automation.template.includeAttributes returns null from GraphQL (backend bug),
      // but the jobTemplates list (fromList) has the correct current values.
      // jobAttributeIds works fine in automation.template.
      // This pattern matches automations.$automationId.tsx (view route lines 49-67).

      // Start with nested template to preserve task-specific data (assets, etc.)
      // Then selectively overwrite with authoritative template data from jobTemplates list
      const merged: JobTemplateFieldsFragment = {
        ...automationTemplate,
        ...fromList,
        // CRITICAL: Preserve includeAttributes and jobAttributeIds from fromList if available,
        // otherwise fall back to automation.template values to prevent loss.
        // Backend bug: automation.template.includeAttributes can be null even when attributes exist.
        // The jobTemplates list (fromList) is the source of truth, but only if the field exists
        // (after codegen runs with updated fragment).
        includeAttributes:
          fromList.includeAttributes !== undefined
            ? fromList.includeAttributes
            : automation.template.includeAttributes,
        jobAttributeIds:
          fromList.jobAttributeIds !== undefined
            ? fromList.jobAttributeIds
            : automation.template.jobAttributeIds,
      };

      return merged;
    }, [automation, templates]);

  // Extract contacts and find current user contact for default owner
  const contacts = getFragmentData(ContactFields, data.contacts);
  const currentUserContact = contacts.find((contact) => contact.isCurrentUser);

  // Store initial currentUserContact in a ref to prevent reset when contacts update reactively.
  // Only update this ref when the automation changes (new automation or switching automations).
  // This ensures that if data.contacts updates after the form is already being edited,
  // the defaultValues won't change and won't trigger a reset that wipes in-progress edits.
  const currentUserContactRef =
    useRef<typeof currentUserContact>(currentUserContact);
  // Use a sentinel value instead of undefined so the first comparison in the effect is true
  const INITIAL_AUTOMATION_ID_SENTINEL = '__INITIAL_AUTOMATION_ID__';
  const previousAutomationIdRef = useRef<
    string | typeof INITIAL_AUTOMATION_ID_SENTINEL
  >(INITIAL_AUTOMATION_ID_SENTINEL);

  // Update ref only when automation ID changes (new automation or switching automations)
  // This captures the currentUserContact at the time the automation loads/changes
  useEffect(() => {
    // Update ref when automation ID changes (including initial mount)
    if (automation?.id && previousAutomationIdRef.current !== automation.id) {
      currentUserContactRef.current = currentUserContact;
      previousAutomationIdRef.current = automation.id;
    }
  }, [automation?.id, currentUserContact]);

  const defaultValues = useMemo(() => {
    // Build templateData from mergedAutomationTemplate if available
    let templateData: FormValues['templateData'] = undefined;

    if (automation?.template && mergedAutomationTemplate) {
      const templateSource = mergedAutomationTemplate;
      const taskInputs = templateSource.tasks.map(taskTemplateToTaskInput);

      const includeAttributes =
        'includeAttributes' in templateSource &&
        templateSource.includeAttributes !== undefined
          ? templateSource.includeAttributes
          : null;

      const jobAttributeIds =
        'jobAttributeIds' in templateSource &&
        templateSource.jobAttributeIds !== undefined
          ? templateSource.jobAttributeIds
          : null;

      templateData = {
        name: templateSource.name,
        notes: safelyDeserializeTemplateNotes(
          templateSource.notes,
          templateSource.id
        ),
        tasks: taskInputs,
        tags: templateSource.tags?.map((tag) => tag.id) || [],
        includeAttributes,
        jobAttributeIds,
        attachments:
          templateSource.attachments?.map((att) => ({
            id: att.id,
            kind: att.kind,
            ext: att.ext,
            thumb: att.thumb,
            url: att.url,
            originalFilename: att.originalFilename,
          })) || [],
      };
    }

    return {
      status: automation?.status ?? Status.Active,
      name: automation?.name ?? '',
      on: automation?.on ?? 'check-out',
      whereType: automation?.whereType ?? AutomationWhereType.All,
      matchAttribute: automation?.matchAttribute
        ? automation.matchAttribute.map(({ ...a }) => a)
        : [],
      sites: automation?.sites ?? [],
      // TODO BACKEND: includeFuturePlaces field not yet in schema - defaults to true
      // When true and all sites selected, automatically includes new sites added in the future
      // includeFuturePlaces: automation?.includeFuturePlaces ?? true,
      jobName: automation?.jobName ?? '',
      template: automation?.template?.id ?? '',
      owner: automation?.owners
        ? automation.owners.map((owner) => owner.id)
        : currentUserContactRef.current
        ? [currentUserContactRef.current.id]
        : null,
      assignee: automation?.assignees
        ? automation.assignees.map((assignee) => assignee.id)
        : null,
      // Backend uses signed integer for 'day': negative=before, positive=after, 0=same day
      day: automation?.day ?? 0,
      time: automation?.time ?? null,
      runOnExistingBookings: automation ? '' : 'true',
      repeatStartDate: (() => {
        if (!automation?.repeatStartDate) return null;
        try {
          const date = new Date(automation.repeatStartDate);
          // Validate the date is valid before formatting
          if (!isNaN(date.getTime())) {
            return formatISO(date, {
              representation: 'date',
            });
          }
        } catch (error) {
          console.error('Error parsing repeatStartDate:', error);
        }
        return null;
      })(),
      repeat: (() => {
        const freq = normalizeFreq(automation?.repeat?.freq);
        const interval = automation?.repeat?.interval ?? '1';

        // Map backend monthly shapes back to UI single-select string value
        const uiMonthSelector =
          freq === 'monthly'
            ? parseMonthlyRepeatToUiSelector(automation?.repeat)
            : null;

        return {
          freq,
          interval,
          byday: normalizeByDay(automation?.repeat?.byday),
          bymonthday: uiMonthSelector,
        };
      })(),
      maintainRepeats: automation?.maintainRepeats ?? 1,
      // TODO BACKEND: avoidBookingOverlap field not yet in schema - feature commented out in UI
      avoidBookingOverlap: automation?.avoidBookingOverlap ?? false,
      // Include templateData in defaultValues to prevent false dirty state
      templateData,
      // Asset-driven template: extract selected asset IDs from tasks
      selectedAssetIds: extractSpecificAssetIds(templateData?.tasks),
    };
  }, [automation, mergedAutomationTemplate]);

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
    setValue,
    getValues,
    reset,
    trigger: formTrigger,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  // Handle this state seperately to keep the form control matching AutomationInput
  const [trigger, setTrigger] = useState<'booking' | 'date'>(
    automation?.on === 'repeat' ? 'date' : 'booking'
  );

  // Reset form and component state when automation data changes (navigating between automations)
  useEffect(() => {
    // Reset form with new default values (now includes templateData and selectedAssetIds)
    reset(defaultValues);

    // Reset trigger state based on new automation
    const newTrigger = automation?.on === 'repeat' ? 'date' : 'booking';
    setTrigger(newTrigger);

    // Reset panel to default based on whether editing or creating
    const hasAutomation = !!automation?.id;
    setOpenPanelIdx(hasAutomation ? 4 : 0);

    // Reset dialog states
    setShowCreateTemplateDialog(false);
    setAnimateFieldId(null);

    // NOTE: templateData and selectedAssetIds are now initialized in defaultValues, no need for setValue here
  }, [automation?.id, automation?.on, defaultValues, reset]); // Reset when automation ID, trigger, or defaultValues change

  // Only required for attribute filters (whereType = 'match') - Site select performs its own query
  const [allSites] = useSites({ requestPolicy: 'cache-first' });
  const attributeFilters = useMemo(
    () => getAttributeFilterOptions(allSites),
    [allSites]
  );

  const [openPanelIdx, setOpenPanelIdx] = useState<number>(() => {
    if (automation) return 4; // Open "What" when editing existing automation
    return 0; // Open "Trigger" when creating new automation
  });
  const [animateFieldId, setAnimateFieldId] = useState<string | null>(null);

  // State for create template dialog
  const [showCreateTemplateDialog, setShowCreateTemplateDialog] =
    useState(false);
  const [, createJobTemplate] = useMutation(CreateJobTemplateDocument);

  // Local task editing state for inline task editor (CustomTaskForm)
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  // taskBeingEditedType is separate state to track type during editing (before form save)
  const [taskBeingEditedType, setTaskBeingEditedType] = useState<string | null>(
    null
  );
  // Track task version to force CustomTaskForm remount when task content changes
  const [taskVersion, setTaskVersion] = useState<number>(0);

  // Convert jobTemplates fragment data to options for FindAddTemplate
  const templateOptions = useMemo(() => {
    return data.jobTemplates.map((template) => {
      const templateData = getFragmentData(JobTemplateFields, template);
      return {
        value: templateData.id,
        label: templateData.name,
      };
    });
  }, [data.jobTemplates]);

  // Extract jobTemplates fragments for grouping in TemplateSelect
  const jobTemplatesFragments = useMemo(() => {
    return data.jobTemplates.map((template) =>
      getFragmentData(JobTemplateFields, template)
    );
  }, [data.jobTemplates]);

  // Validation schema for template name (check uniqueness)
  const templateNameSchema = useMemo(() => {
    const existingNames = templateOptions.map((opt) =>
      opt.label.toLowerCase().trim()
    );
    return object({
      name: string()
        .required('Template name is required')
        .test('unique', 'A template with this name already exists', (value) => {
          if (!value) return true;
          return !existingNames.includes(value.toLowerCase().trim());
        }),
    });
  }, [templateOptions]);

  // Form for template name (defined after schema)
  const {
    register: registerTemplateName,
    handleSubmit: handleSubmitTemplateName,
    formState: { errors: templateNameErrors, isSubmitting: isCreatingTemplate },
    reset: resetTemplateName,
  } = useForm<{ name: string }>({
    defaultValues: { name: '' },
    resolver: yupResolver(templateNameSchema),
  });

  // Handler for creating new template
  const handleCreateTemplate = useCallback(
    async (name: string) => {
      const currentFormData = getValues();
      const templateData = currentFormData.templateData;
      if (!templateData) {
        toast.error('No template data to save');
        return;
      }

      try {
        // Convert template data to JobTemplateInput
        // Convert attachments to UploadResult format (toInput expects UploadResult[])
        const attachmentsAsUploadResult = templateData.attachments.map(
          (att) => ({
            id: att.id,
            kind: att.kind as 'file' | 'image',
            name: att.originalFilename,
            preview: att.thumb,
          })
        );

        const templateInput = toInput(templateData.tasks, {
          name: name.trim(),
          notes: templateData.notes ?? null,
          tags: templateData.tags.length ? templateData.tags : null,
          includeAttributes: templateData.includeAttributes,
          // GraphQL input uses jobAttributes; our form state uses jobAttributeIds
          jobAttributes: templateData.jobAttributeIds ?? undefined,
          attachments: attachmentsAsUploadResult,
          status: Status.Active,
          guestyCleaningStatus: false,
        });

        const result = await createJobTemplate({ input: templateInput });

        if (result.error) {
          toast.error(
            'Failed to create template: ' +
              result.error.message.replace('[GraphQL] ', '')
          );
          return;
        }

        if (!result.data?.createJobTemplate?.id) {
          toast.error('Failed to create template. Please try again.');
          return;
        }

        const newTemplateId = result.data.createJobTemplate.id;
        setValue('template', newTemplateId);

        // Update templateData with the new template name to ensure fields display
        const latestFormData = getValues();
        const currentTemplateData = latestFormData.templateData;
        if (currentTemplateData) {
          setValue('templateData', {
            ...currentTemplateData,
            name: name.trim(),
          });
        }

        toast.success('New template created successfully');
        setShowCreateTemplateDialog(false);
        resetTemplateName();

        // Notify parent that template was created
        const finalFormData = getValues();
        onTemplateCreated?.(newTemplateId, finalFormData);
      } catch (error) {
        console.error('Error creating template:', error);
        toast.error('An unexpected error occurred. Please try again.');
      }
    },
    [
      getValues,
      createJobTemplate,
      setValue,
      resetTemplateName,
      setShowCreateTemplateDialog,
      onTemplateCreated,
    ]
  );

  const selectedTemplateId = watch('template');
  // Use useWatch for reactive updates when template data changes
  const templateData = useWatch({
    control,
    name: 'templateData',
  });
  // Use useWatch to only trigger re-renders when tasks actually change
  const templateTasks = useWatch({
    control,
    name: 'templateData.tasks',
  });

  // Derive taskBeingEdited from templateTasks using useMemo (same pattern as JobTemplateForm)
  // This avoids creating new object references on every templateTasks change
  const taskBeingEdited = useMemo(() => {
    if (!editTaskId) return null;
    const currentTasks = templateTasks || [];
    return currentTasks.find((t) => t.id === editTaskId) || null;
  }, [editTaskId, templateTasks]);

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.id === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  // Determine template behavior for asset-driven templates
  const templateBehavior = useMemo(() => {
    return getTemplateBehavior(selectedTemplate);
  }, [selectedTemplate]);

  const baselineTemplate: JobTemplateFieldsFragment | null = useMemo(() => {
    if (selectedTemplateId) {
      if (
        mergedAutomationTemplate &&
        mergedAutomationTemplate.id === selectedTemplateId
      ) {
        return mergedAutomationTemplate;
      }
      const fromList = templates.find((t) => t.id === selectedTemplateId);
      if (fromList) {
        return fromList;
      }
    }

    return mergedAutomationTemplate ?? null;
  }, [selectedTemplateId, mergedAutomationTemplate, templates]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (showCreateTemplateDialog) {
      resetTemplateName();
    }
  }, [showCreateTemplateDialog, resetTemplateName]);

  // Handle external trigger to show create dialog
  useEffect(() => {
    if (externalShowCreateDialog) {
      setShowCreateTemplateDialog(true);
    }
  }, [externalShowCreateDialog]);

  const [allTags] = useTags(TagEntityType.Job);

  // Fetch all assets for asset-driven templates
  const [allAssets] = useAllAssets();

  // Check if site attributes exist
  const [siteAttributesResult] = useQuery({
    query: AttributeSelect_Query,
    variables: { entityType: 'Site' },
    requestPolicy: 'cache-first',
  });

  // Check if job attributes exist
  const [jobAttributesResult] = useQuery({
    query: AttributeSelect_Query,
    variables: { entityType: 'Job' },
    requestPolicy: 'cache-first',
  });

  const hasSiteAttributes =
    !siteAttributesResult.fetching &&
    (siteAttributesResult.data?.attributes?.length ?? 0) > 0;

  const hasJobAttributes =
    !jobAttributesResult.fetching &&
    (jobAttributesResult.data?.attributes?.length ?? 0) > 0;

  // Initialize templateData when ANY template is selected
  useEffect(() => {
    // When editing an existing automation, prefer the automation.template instance
    // for initializing templateData. It can include extra task data (e.g. assets)
    // that might not be present on the generic jobTemplates list.
    // We merge automation.template with the authoritative template from jobTemplates to ensure
    // includeAttributes / jobAttributeIds are accurate even if the automation's nested template is stale.
    const automationTemplate =
      mergedAutomationTemplate &&
      mergedAutomationTemplate.id === selectedTemplateId
        ? mergedAutomationTemplate
        : null;
    const templateSource = automationTemplate || selectedTemplate;

    if (templateSource) {
      const behavior = getTemplateBehavior(templateSource);

      // For asset-driven templates, don't initialize tasks from template
      // They will be generated from selected assets in a separate effect
      const taskInputs =
        behavior === 'asset_rules'
          ? []
          : templateSource.tasks.map(taskTemplateToTaskInput);

      // Handle includeAttributes: preserve arrays (including empty), convert undefined to null
      // This ensures that empty arrays [] are preserved, not converted to null
      const includeAttributes =
        'includeAttributes' in templateSource &&
        templateSource.includeAttributes !== undefined
          ? templateSource.includeAttributes
          : null;

      // Handle jobAttributeIds: same logic as includeAttributes
      const jobAttributeIds =
        'jobAttributeIds' in templateSource &&
        templateSource.jobAttributeIds !== undefined
          ? templateSource.jobAttributeIds
          : null;

      const templateData = {
        name: templateSource.name,
        notes: safelyDeserializeTemplateNotes(
          templateSource.notes,
          templateSource.id
        ),
        tasks: taskInputs,
        tags: templateSource.tags?.map((tag) => tag.id) || [],
        includeAttributes,
        jobAttributeIds,
        attachments:
          templateSource.attachments?.map((att) => ({
            id: att.id,
            kind: att.kind,
            ext: att.ext,
            thumb: att.thumb,
            url: att.url,
            originalFilename: att.originalFilename,
          })) || [],
      };

      // User changed template selection - mark as dirty (this is a real user action)
      setValue('templateData', templateData);

      // For asset-driven templates, clear selected assets when switching templates
      // (unless we're loading an existing automation with the same template)
      if (
        behavior === 'asset_rules' &&
        !(automationTemplate && automationTemplate.id === selectedTemplateId)
      ) {
        setValue('selectedAssetIds', []);
      }
    } else if (selectedTemplateId && !templateSource) {
      // Template ID is set but template not found in list (e.g., newly created)
      // Preserve templateData - it should already be set from creation
      // Do nothing to avoid clearing it
    } else if (!selectedTemplateId) {
      // Clear templateData when no template selected
      setValue('templateData', undefined);
      setValue('selectedAssetIds', []);
    }
  }, [
    mergedAutomationTemplate,
    selectedTemplate,
    selectedTemplateId,
    setValue,
  ]);

  const whereType = watch('whereType');
  const matchAttribute = watch('matchAttribute');
  const selectedSites = watch('sites');
  const owner = watch('owner');
  const assignee = watch('assignee');
  const on = watch('on');
  const repeatFreq = watch('repeat.freq');
  const repeatInterval = watch('repeat.interval');
  const jobName = watch('jobName');
  const name = watch('name');
  const day = watch('day');
  const selectedBymonthday = watch('repeat.bymonthday');
  const selectedAssetIds = watch('selectedAssetIds');

  // Derive scopeIds and locationId for asset task configuration (same rules as CreateJobForm)
  // Memoize to prevent new array identity on every render
  const scopeIds = useMemo(
    () => (selectedSites || []).map((site) => site.id),
    [selectedSites]
  );
  // CustomTaskForm's SpacesSelect expects bare location ID and adds Site: prefix itself
  // So we pass the bare ID, not the prefixed format
  const locationId = scopeIds.length === 1 ? scopeIds[0] : undefined;

  // Sync taskBeingEditedType when editTaskId changes (opening a different task)
  // We only sync on editTaskId change, NOT on templateTasks change, to avoid
  // resetting the type while the user is actively editing via dropdown
  useEffect(() => {
    if (!editTaskId) {
      setTaskBeingEditedType(null);
      return;
    }

    // Read directly from form state to get task type when opening a task
    const currentFormData = getValues();
    const currentTasks = currentFormData.templateData?.tasks || [];
    const task = currentTasks.find((t) => t.id === editTaskId);
    setTaskBeingEditedType(task?.type ?? null);
  }, [editTaskId, getValues]);

  // Close inline task editor if the currently edited task is no longer present
  // in the underlying templateTasks array (e.g. when template changes or tasks reset).
  useEffect(() => {
    if (!editTaskId) {
      return;
    }

    const currentTasks = templateTasks || [];
    const taskStillExists = currentTasks.some((task) => task.id === editTaskId);

    if (!taskStillExists) {
      setEditTaskId(null);
    }
  }, [editTaskId, templateTasks]);

  // Helper to check if a task is the default rule task
  // Only undefined means default rule, not empty array (empty = user hasn't selected types yet)
  const isDefaultRuleTask = useCallback((task: TaskInput): boolean => {
    return (
      task.type === 'Asset' &&
      task.asset?.selection === 'all' &&
      task.asset?.assetTypes === undefined // Only undefined, not empty array
    );
  }, []);

  // Helper to create the default rule task
  const createDefaultRuleTask = useCallback((): TaskInput => {
    const editorState = EditorState.createEmpty();
    const defaultDescription = convertToRaw(editorState.getCurrentContent());

    return {
      id: nanoid(),
      type: 'Asset',
      name: 'Default Task',
      description: JSON.stringify(defaultDescription),
      asset: {
        selection: 'all',
        assetTypes: undefined, // undefined means match all asset types
        allow: ['edit'],
      },
    };
  }, []);

  // Create/remove default task when templateBehavior changes
  // For asset-driven templates, automatically create a default "catch-all" task
  // that matches all asset types (selection: 'all', assetTypes: undefined)
  // Use ref to detect actual transitions and avoid infinite loops
  const previousBehaviorRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Only run when templateBehavior actually transitions (not on every render)
    if (previousBehaviorRef.current === templateBehavior) {
      return;
    }

    const previousBehavior = previousBehaviorRef.current;
    previousBehaviorRef.current = templateBehavior;

    // Skip initial render (when previousBehavior is undefined)
    if (previousBehavior === undefined) {
      return;
    }

    // Read current tasks from form state (not from prop to avoid infinite loop)
    const currentTasks = getValues('templateData.tasks') || [];

    if (templateBehavior === 'asset_rules') {
      // Asset-driven template: ensure default task exists
      const hasDefaultTask = currentTasks.some(
        (task) => !task._destroy && isDefaultRuleTask(task)
      );

      if (!hasDefaultTask) {
        // Add default task if it doesn't exist
        // Guard: only update if we're actually adding a task
        const newDefaultTask = createDefaultRuleTask();
        const intendedTasks = [...currentTasks, newDefaultTask];
        setValue('templateData.tasks', intendedTasks);
      }
    } else {
      // Not asset-driven: remove default task if it exists
      const hasDefaultTask = currentTasks.some(
        (task) => !task._destroy && isDefaultRuleTask(task)
      );

      if (hasDefaultTask) {
        // Remove default task
        // Guard: only update if we're actually removing tasks
        const filteredTasks = currentTasks.filter(
          (task) => task._destroy || !isDefaultRuleTask(task)
        );
        // Only call setValue if the result is different
        if (filteredTasks.length !== currentTasks.length) {
          setValue('templateData.tasks', filteredTasks);
        }
      }
    }
  }, [
    templateBehavior,
    setValue,
    getValues,
    isDefaultRuleTask,
    createDefaultRuleTask,
  ]);

  // Regenerate tasks when assets are selected for asset-driven templates
  // This effect runs when selectedAssetIds changes and we have an asset-driven template
  useEffect(() => {
    if (!selectedTemplate || templateBehavior !== 'asset_rules') {
      return;
    }

    // Get selected assets from IDs
    const selectedAssets = allAssets.filter((asset) =>
      selectedAssetIds?.includes(asset.id)
    );

    // Read current tasks from form state (not from prop to avoid infinite loop)
    const currentTasks = getValues('templateData.tasks') || [];

    if (selectedAssets.length === 0) {
      // No assets selected, keep only the default task
      const defaultTask = currentTasks.find(
        (task) => !task._destroy && isDefaultRuleTask(task)
      );
      const intendedTasks = defaultTask ? [defaultTask] : [];
      // Only update if different from current
      if (
        intendedTasks.length !== currentTasks.length ||
        (intendedTasks.length === 1 &&
          currentTasks.length === 1 &&
          intendedTasks[0]?.id !== currentTasks[0]?.id)
      ) {
        setValue('templateData.tasks', intendedTasks);
      }
      return;
    }

    // Generate tasks from asset-driven template
    const generatedTasks = generateTasksFromAssetDrivenTemplate(
      selectedTemplate,
      selectedAssets
    );

    // Preserve the default task and any user-created tasks
    const defaultTask = currentTasks.find(
      (task) => !task._destroy && isDefaultRuleTask(task)
    );
    // Also preserve any tasks that aren't from the template (user-created)
    // Cast to extended type since generateTasksFromAssetDrivenTemplate adds templateTaskId
    const userCreatedTasks = currentTasks.filter(
      (task) =>
        !task._destroy &&
        !isDefaultRuleTask(task) &&
        !(task as TaskInput & { templateTaskId?: string }).templateTaskId // Tasks without templateTaskId are user-created
    );

    // Update templateData.tasks with generated tasks + default task + user-created tasks
    const finalTasks = [
      ...generatedTasks,
      ...(defaultTask ? [defaultTask] : []),
      ...userCreatedTasks,
    ];
    // Only update if different from current (compare lengths and IDs)
    const currentTaskIds = currentTasks
      .filter((t) => !t._destroy)
      .map((t) => t.id)
      .sort();
    const finalTaskIds = finalTasks.map((t) => t.id).sort();
    if (
      currentTaskIds.length !== finalTaskIds.length ||
      currentTaskIds.some((id, i) => id !== finalTaskIds[i])
    ) {
      setValue('templateData.tasks', finalTasks);
    }
  }, [
    selectedAssetIds,
    selectedTemplate,
    templateBehavior,
    allAssets,
    setValue,
    getValues,
    isDefaultRuleTask,
  ]);

  const now = useMemo(() => new Date(), []);
  const scheduledStartDate = watch('repeatStartDate');
  // scheduledStartDate is a YYYY-MM-DD string (property-local)
  // Parse it directly without timezone conversion to avoid browser timezone issues
  const formStartDate = useMemo(() => {
    if (!scheduledStartDate) return now;
    try {
      // Parse YYYY-MM-DD string directly - this creates a Date at midnight local time
      // but we only use it for getDate(), getDay(), getWeekNumber() which don't depend on timezone
      const parsed = parse(scheduledStartDate, 'yyyy-MM-dd', new Date());
      if (isNaN(parsed.getTime())) {
        return now;
      }
      return parsed;
    } catch (error) {
      console.warn('Invalid scheduledStartDate:', scheduledStartDate, error);
      return now;
    }
  }, [scheduledStartDate, now]);
  const startDateWeekNumber = getWeekNumber(formStartDate);

  // Generate base options based on current start date
  const baseMonthOptions = useMemo(
    () => [
      {
        value: `bymonthday:${formStartDate.getDate()}`,
        label: `Day ${formStartDate.getDate()} of the month`,
      },
      {
        value: `byday:${startDateWeekNumber},${DAYS[formStartDate.getDay()]}`,
        label: `${
          startDateWeekNumber > 4
            ? 'Last'
            : getOrdinalSuffix(startDateWeekNumber)
        } ${DAYS[formStartDate.getDay()]} of the month`,
      },
    ],
    [formStartDate, startDateWeekNumber]
  );

  // Ensure selected value is included in options (formatted if needed)
  const monthOptions = useMemo(() => {
    if (!selectedBymonthday) return baseMonthOptions;

    // Check if selected value is already in base options
    const isInOptions = baseMonthOptions.some(
      (opt) => opt.value === selectedBymonthday
    );

    if (isInOptions) {
      return baseMonthOptions;
    }

    // Selected value is not in base options, add it as a formatted option
    const formattedLabel = formatMonthlyPatternValue(selectedBymonthday);
    return [
      ...baseMonthOptions,
      {
        value: selectedBymonthday,
        label: formattedLabel || selectedBymonthday,
      },
    ];
  }, [baseMonthOptions, selectedBymonthday]);

  // Computed summaries for accordion buttons
  const triggerInfo = useMemo(() => {
    if (trigger === 'booking') return 'New or Changed Bookings';
    if (trigger === 'date') return 'A date or time interval';
    return null;
  }, [trigger]);

  // TODO BACKEND: includeFuturePlaces field not yet in schema - feature commented out in UI
  // const includeFuturePlaces = watch('includeFuturePlaces');

  const whereInfo = useMemo(() => {
    if (whereType === AutomationWhereType.Match) {
      return matchAttribute?.length > 0
        ? `${matchAttribute.length} attribute filter${
            matchAttribute.length > 1 ? 's' : ''
          }`
        : null;
    }
    if (whereType === AutomationWhereType.Select) {
      // Check if all sites are selected
      const allSelected =
        selectedSites?.length === allSites.length && allSites.length > 0;

      // TODO BACKEND: includeFuturePlaces field not yet in schema - feature commented out in UI
      //if (allSelected && includeFuturePlaces) {
      //  return 'All Places (including future)';
      //}
      if (allSelected) {
        return 'All Places';
      }
      return selectedSites?.length > 1
        ? `${selectedSites.length} Selected`
        : selectedSites?.length === 1
        ? selectedSites[0].name
        : null;
    }
    return null;
  }, [whereType, matchAttribute, selectedSites, allSites]); // TODO BACKEND: includeFuturePlaces field not yet in schema - feature commented out in UI

  const whoInfo = useMemo(() => {
    const parts = [];
    if (owner?.length)
      parts.push(`${owner.length} owner${owner.length > 1 ? 's' : ''}`);
    if (assignee?.length)
      parts.push(
        `${assignee.length} assignee${assignee.length > 1 ? 's' : ''}`
      );
    return parts.length > 0 ? parts.join(', ') : null;
  }, [owner, assignee]);

  const whenInfo = useMemo(() => {
    if (trigger === 'date') {
      return `Every ${repeatInterval} ${t(`repeatFrequencies.${repeatFreq}`)}`;
    }

    // Booking trigger (using day offset)
    const offset = typeof day === 'number' ? day : 0;
    const eventLabel = on ? t(`automations.events.${on}`) : '';
    if (offset === 0) return `Same day as ${eventLabel}`;
    const abs = Math.abs(offset);
    const positionLabel = offset > 0 ? 'after' : 'before';
    return `${abs} day${abs > 1 ? 's' : ''} ${positionLabel} ${eventLabel}`;
  }, [trigger, on, day, repeatFreq, repeatInterval, t]);

  const whatInfo = useMemo(() => {
    return jobName || name || null;
  }, [jobName, name]);

  // Panel toggle with validation
  const handlePanelToggle = useCallback(
    async (panelIndex: number | undefined) => {
      const nextPanelIndex = panelIndex === undefined ? -1 : panelIndex;

      if (nextPanelIndex !== openPanelIdx) {
        // Validate fields based on which panel is currently open
        switch (openPanelIdx) {
          case 0: // Trigger panel
            // No validation needed for trigger selection
            break;
          case 1: // When panel
            await formTrigger(['day', 'time']);
            if (trigger === 'date') {
              await formTrigger('repeat');
            }
            break;
          case 2: // Where panel
            if (whereType === AutomationWhereType.Select) {
              await formTrigger('sites');
            } else if (whereType === AutomationWhereType.Match) {
              await formTrigger('matchAttribute');
            }
            break;
          case 4: // What panel
            await formTrigger(['name', 'jobName', 'template']);
            break;
        }
      }

      setOpenPanelIdx(nextPanelIndex);

      // Scroll to panel
      if (panelIndex !== null && panelIndex !== undefined) {
        setTimeout(() => {
          const panels = document.querySelectorAll('.greycard');
          panels[panelIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 300);
      }
    },
    [openPanelIdx, whereType, trigger, formTrigger]
  );

  // Stable callbacks for ManageTasks to prevent unnecessary re-renders
  const handleTasksUpdate = useCallback(
    (newTasks: TaskInput[] | ((prev: TaskInput[]) => TaskInput[])) => {
      // Use getValues to get the latest form state instead of relying on closure
      // This prevents recreating the callback when templateTasks changes (from useWatch)
      const currentFormData = getValues();
      const baseTasks = currentFormData.templateData?.tasks || [];
      const tasksArray =
        typeof newTasks === 'function' ? newTasks(baseTasks) : newTasks;
      setValue('templateData.tasks', tasksArray);
    },
    [getValues, setValue]
  );

  const handleShowTask = useCallback(
    (taskId: string) => {
      setEditTaskId(taskId);

      // Fetch the latest task data from form state to set the type
      // taskBeingEdited is derived via useMemo from templateTasks and editTaskId
      const currentFormData = getValues();
      const currentTasks = currentFormData.templateData?.tasks || [];
      const task = currentTasks.find((t) => t.id === taskId);

      if (task) {
        setTaskBeingEditedType(task.type ?? null);
      }

      // Increment version to ensure CustomTaskForm remounts with latest task data
      // This is important when reopening a task after it was updated
      setTaskVersion((prev) => prev + 1);
    },
    [getValues]
  );

  const handleTaskTypeUpdate = useCallback(
    (id: string, type: string) => {
      // Use getValues to get the latest form state instead of relying on closure
      // This prevents recreating the callback when templateTasks changes (from useWatch)
      const currentFormData = getValues();
      const currentTasks = currentFormData.templateData?.tasks || [];
      const updatedTasks = currentTasks.map((task) =>
        task.id === id ? { ...task, type } : task
      );
      setValue('templateData.tasks', updatedTasks);
    },
    [getValues, setValue]
  );

  // Stable callbacks for CustomTaskForm to prevent re-renders
  const handleTaskEditorUpdateTaskType = useCallback(
    (id: string, type: string) => {
      if (id === taskBeingEdited?.id) {
        setTaskBeingEditedType(type);
        // Use getValues to get the latest form state instead of relying on closure
        // This prevents recreating the callback when templateTasks changes (from useWatch)
        const currentFormData = getValues();
        const currentTasks = currentFormData.templateData?.tasks || [];
        const updatedTasks = currentTasks.map((task) =>
          task.id === id ? { ...task, type } : task
        );
        setValue('templateData.tasks', updatedTasks);
      }
    },
    [taskBeingEdited?.id, getValues, setValue]
  );

  const handleTaskEditorApply = useCallback(
    (updatedTask: TaskInput) => {
      // Use getValues to get the latest form state instead of relying on closure
      const currentFormData = getValues();
      const currentTasks = currentFormData.templateData?.tasks || [];
      const updatedTasks = currentTasks.map((task) =>
        task.id === updatedTask.id ? updatedTask : task
      );
      setValue('templateData.tasks', updatedTasks);

      // Close the editor - taskBeingEdited will be cleared by the sync effect
      // When reopening via handleShowTask, it will fetch the latest data from form state
      setEditTaskId(null);

      // Increment version to force CustomTaskForm remount with updated task data
      // when reopening (handleShowTask will fetch latest data and increment version again)
      setTaskVersion((prev) => prev + 1);
    },
    [getValues, setValue]
  );

  const handleTaskEditorClose = useCallback(() => {
    setEditTaskId(null);
  }, []);

  const handleTaskEditorDelete = useCallback(
    (id: string) => {
      // Use getValues to get the latest form state instead of relying on closure
      const currentFormData = getValues();
      const currentTasks = currentFormData.templateData?.tasks || [];
      const updatedTasks = currentTasks.map((task) =>
        task.id === id ? { ...task, _destroy: true } : task
      );
      setValue('templateData.tasks', updatedTasks);
      setEditTaskId(null);
    },
    [getValues, setValue]
  );

  const hasTemplateChanges = useMemo(() => {
    if (!baselineTemplate || !templateData) {
      return false;
    }

    try {
      // Pass raw templateData directly - isTemplateModified already handles
      // undefined/null normalization correctly. No need for intermediate normalization.
      // isTemplateModified only inspects the templateData field, so we can safely
      // pass a partial FormValues object with just that field populated.
      const formData = { templateData } as unknown as FormValues;
      const result = isTemplateModified(formData, baselineTemplate);

      return result;
    } catch (error) {
      console.error(
        '[AutomationForm] Error comparing template changes:',
        error
      );
      return false;
    }
  }, [baselineTemplate, templateData]);

  const handleFormSubmit = handleSubmit(
    (formData) => {
      // Validate asset selection for asset-driven templates
      if (
        templateBehavior === 'asset_rules' &&
        (!formData.selectedAssetIds || formData.selectedAssetIds.length === 0)
      ) {
        toast.warning(
          'Please select at least one asset for this asset-driven template',
          { theme: 'colored' }
        );
        // Open the What panel if not already open
        if (openPanelIdx !== 4) {
          setOpenPanelIdx(4);
        }
        return;
      }

      // Attach templateModified flag so routes can rely on this instead of
      // re-running template comparison logic with their own baselines.
      const formWithTemplateFlag: FormValues = {
        ...formData,
        templateModified: hasTemplateChanges,
      };

      // TODO BACKEND: If backend requires data transformation (e.g., converting day names
      // back to numeric codes, or freq strings to numeric codes), implement transformation
      // in the parent component's onSubmit handler before sending mutation to backend.
      // Current assumption: Backend accepts the FormValues structure as-is.
      return onSubmit(formWithTemplateFlag);
    },
    (errors) => {
      const errorMessage = 'Please fill out all required fields';
      toast.warning(errorMessage, { theme: 'colored' });

      // Find the first panel with an error
      let firstErrorPanel = null;
      if (errors.day || errors.time || errors.repeat) {
        firstErrorPanel = 1; // WHEN panel
      } else if (errors.sites || errors.matchAttribute) {
        firstErrorPanel = 2; // WHERE panel
      } else if (errors.owner || errors.assignee) {
        firstErrorPanel = 3; // WHO panel
      } else if (errors.name || errors.jobName || errors.template) {
        firstErrorPanel = 4; // WHAT panel
      }

      if (firstErrorPanel !== null) {
        const currentPanelHasError =
          (errors.day && openPanelIdx === 1) ||
          (errors.time && openPanelIdx === 1) ||
          (errors.repeat && openPanelIdx === 1) ||
          (errors.sites && openPanelIdx === 2) ||
          (errors.matchAttribute && openPanelIdx === 2) ||
          (errors.owner && openPanelIdx === 3) ||
          (errors.assignee && openPanelIdx === 3) ||
          (errors.name && openPanelIdx === 4) ||
          (errors.jobName && openPanelIdx === 4) ||
          (errors.template && openPanelIdx === 4);

        if (currentPanelHasError) {
          // Animate the error field
          if (openPanelIdx === 1 && errors.day) {
            setAnimateFieldId('day');
          } else if (openPanelIdx === 2 && errors.sites) {
            setAnimateFieldId('sites');
          } else if (openPanelIdx === 2 && errors.matchAttribute) {
            setAnimateFieldId('matchAttribute');
          } else if (openPanelIdx === 4 && errors.name) {
            setAnimateFieldId('name');
          } else if (openPanelIdx === 4 && errors.jobName) {
            setAnimateFieldId('jobName');
          } else if (openPanelIdx === 4 && errors.template) {
            setAnimateFieldId('template');
          }
          // Remove animation class after it completes
          setTimeout(() => setAnimateFieldId(null), 1820);
        } else if (!currentPanelHasError && firstErrorPanel !== openPanelIdx) {
          // First close any open panel
          setOpenPanelIdx(-1);

          // Then open the error panel after a small delay
          setTimeout(() => {
            setOpenPanelIdx(firstErrorPanel);

            // Scroll after panel is opened
            setTimeout(() => {
              const panels = document.querySelectorAll('.greycard');
              panels[firstErrorPanel]?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }, 100);
          }, 50);
        }
      }
    }
  );

  // When editing a task, show dedicated task editor as a side sub-outlet (same pattern as CreateJobForm)
  // Use a key based on task ID and version to ensure form resets when task content changes after save
  // Use isJobTemplate={true} to enable specific asset selection like in JobTemplateForm
  // This allows automation templates to select specific assets regardless of site count
  const taskSection = taskBeingEdited ? (
    <CustomTaskForm
      key={`task-${taskBeingEdited.id}-v${taskVersion}`}
      locationId={locationId}
      task={taskBeingEdited}
      taskType={taskBeingEditedType || taskBeingEdited.type}
      updateTaskType={handleTaskEditorUpdateTaskType}
      onApply={handleTaskEditorApply}
      onClose={handleTaskEditorClose}
      onDelete={handleTaskEditorDelete}
      showTaskOptions
      scopeIds={scopeIds}
      isJobTemplate
    />
  ) : null;

  const promptWhen = useMemo(() => {
    const result =
      hasTemplateChanges &&
      !isSubmitting &&
      !showCreateTemplateDialog &&
      !disablePrompt;
    return result;
  }, [
    hasTemplateChanges,
    isSubmitting,
    showCreateTemplateDialog,
    disablePrompt,
  ]);

  return (
    <div className='flex h-full items-stretch justify-end overflow-y-auto'>
      <SideLayout as='form' onSubmit={handleFormSubmit}>
        <Prompt when={promptWhen} cancel='Stay with Form' />
        <SideLayout.Head leftSlot={<Link to='..' className='x' />}>
          {t(['automations', automation ? 'edit' : 'new'].join('.'))}
        </SideLayout.Head>
        <SideLayout.Body>
          <Controller
            name='status'
            control={control}
            render={({ field }) => (
              <ControlledStatusToggle
                checked={field.value === 'Active'}
                onChange={(event) =>
                  field.onChange(event.target.checked ? 'Active' : 'Inactive')
                }
              />
            )}
          />

          <Input
            {...register('name')}
            label={t('automation_name')}
            error={errors.name?.message}
            required
            className={classNames('mb-5', {
              'animate-shake': animateFieldId === 'name',
            })}
          />

          <Accordion
            defaultOpen={openPanelIdx}
            onToggledPanel={handlePanelToggle}
            openPanel={openPanelIdx}
          >
            {/* Trigger Panel - Index 0 */}
            <Accordion.GreyCard>
              <Accordion.Button summary={triggerInfo} help='Automation type'>
                Automation Type
              </Accordion.Button>
              <Accordion.Panel>
                <Accordion.PanelSection>
                  <p className='mb-3 font-medium'>
                    When should this automation run?
                  </p>
                  <RadioGroup
                    name='trigger'
                    title=''
                    options={[
                      {
                        value: 'booking',
                        label: 'When a booking changes',
                        subtitle:
                          'Trigger this automation when a booking is created, updated, or cancelled',
                      },
                      {
                        value: 'date',
                        label: 'On a specific date or schedule',
                        subtitle:
                          'Run this automation on a set date or recurring interval (e.g., every week, every month).',
                      },
                    ]}
                    label='Trigger'
                    value={trigger}
                    onChange={(t) => {
                      setTrigger(t as 'booking' | 'date');
                      if (t === 'date') {
                        setValue('on', 'repeat');
                      } else {
                        setValue('on', 'check-out');
                      }
                    }}
                  />
                </Accordion.PanelSection>
                {trigger === 'booking' && (
                  <Accordion.PanelSection>
                    <p className='font-medium'>Run on Existing Bookings?</p>
                    <p className='mb-3 text-sm text-secondary'>
                      When selected, this will apply to bookings already in the
                      system.
                    </p>
                    <RadioGroup
                      name='runOnExistingBookings'
                      title=''
                      options={[
                        { value: 'true', label: 'Yes' },
                        { value: '', label: 'No' },
                      ]}
                      value={watch('runOnExistingBookings')}
                      onChange={(value) =>
                        setValue('runOnExistingBookings', value)
                      }
                    />
                  </Accordion.PanelSection>
                )}
              </Accordion.Panel>
            </Accordion.GreyCard>

            {/* When Panel - Index 1 */}
            <Accordion.GreyCard
              error={!!(errors.day || errors.time || errors.repeat)}
            >
              <Accordion.Button
                summary={whenInfo}
                help={
                  errors.day
                    ? 'Schedule day is required'
                    : errors.time
                    ? 'Time is required'
                    : errors.repeat
                    ? 'Repeat settings required'
                    : 'Schedule'
                }
                error={!!(errors.day || errors.time || errors.repeat)}
              >
                When
              </Accordion.Button>
              <Accordion.Panel>
                {trigger === 'booking' && (
                  <>
                    {/* Relative to section - First */}
                    <div className='mb-5'>
                      <Accordion.PanelSection>
                        <Controller
                          name='on'
                          control={control}
                          render={({ field }) => (
                            <SegmentedControl
                              title='Relative to'
                              options={[
                                { value: 'check-in', label: 'Check-in' },
                                { value: 'check-out', label: 'Check-out' },
                              ]}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />{' '}
                        {/* Schedule Job section */}
                        <div className='mb-5 mt-5'>
                          <label className='mb-2 block text-sm font-medium'>
                            Start
                          </label>
                          <div className='mb-3'>
                            <Controller
                              name='day'
                              control={control}
                              render={({ field }) => (
                                <SelectCombolist
                                  className='mb-5'
                                  label=''
                                  searchable={false}
                                  options={dayOptions}
                                  optionsBoxHeight={
                                    isMobile
                                      ? 'max-h-[calc(100vh-160px)]'
                                      : 'max-h-[calc(100vh-280px)]'
                                  }
                                  value={
                                    typeof field.value === 'number'
                                      ? String(field.value)
                                      : '0'
                                  }
                                  onChange={(selected) =>
                                    field.onChange(parseInt(selected))
                                  }
                                  required
                                />
                              )}
                            />
                          </div>
                          <Controller
                            control={control}
                            name='time'
                            render={({ field: { value, onChange } }) => (
                              <TimePicker
                                label='At time'
                                value={value ?? ''}
                                onChange={(val) => onChange(val || null)}
                                error={errors.time?.message}
                                leadingIcon
                                showClearIcon
                              />
                            )}
                          />
                        </div>
                        {/* TODO BACKEND: excludeDays feature requires backend scheduling logic to skip certain days */}
                        {/* Exclude days section */}
                        {/* <div className='mb-5'>
                        <Controller
                          name='excludeDays'
                          control={control}
                          render={({ field }) => (
                            <Switch
                              label='Exclude days'
                              checked={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                        {excludeDays && (
                          <div className='mt-4'>
                            <label className='mb-2 block text-sm font-medium'>
                              Do not schedule on these days
                            </label>
                            <Controller
                              control={control}
                              name='excludeDaysList'
                              render={({ field }) => (
                                <div className='mb-3 flex items-center justify-between gap-0 sm:justify-start sm:gap-1 lg:gap-2'>
                                  {DAYS_DISPLAY_ORDER.map((dayName, index) => (
                                    <div
                                      className='rounded-full'
                                      onClick={() => {
                                        const values = [...(field.value ?? [])];
                                        field.onChange(
                                          values.includes(dayName)
                                            ? values.filter(
                                                (val) => val !== dayName
                                              )
                                            : [...values, dayName]
                                        );
                                      }}
                                      key={index}
                                    >
                                      <Avatar
                                        className={classNames(
                                          '!h-10 cursor-pointer',
                                          {
                                            '!bg-brand text-white':
                                              field.value?.includes(dayName),
                                          }
                                        )}
                                        name={dayName}
                                        size='medium'
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            />
                            <label className='mb-2 block text-sm font-medium'>
                              Schedule instead on the day
                            </label>
                            <div className='mb-3 flex gap-2'>
                              <Controller
                                name='excludeFallback'
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    hasSelectorIcon
                                    label=''
                                    options={[
                                      { value: 'after', label: 'After' },
                                      { value: 'before', label: 'Before' },
                                    ]}
                                    value={field.value ? [field.value] : []}
                                    onChange={(selected) =>
                                      field.onChange(
                                        selected.length ? selected[0] : 'after'
                                      )
                                    }
                                  />
                                )}
                              />
                            </div>
                            <Controller
                              control={control}
                              name='excludeTime'
                              render={({ field: { value, onChange } }) => (
                                <TimePicker
                                  label='At time'
                                  value={value ?? ''}
                                  onChange={(val) => onChange(val || null)}
                                />
                              )}
                            />
                          </div>
                        )}
                      </div> */}
                      </Accordion.PanelSection>
                    </div>
                  </>
                )}
                {trigger === 'date' && (
                  <>
                    {/* Start Date and Time */}
                    <div className='mb-5 grid grid-cols-2 gap-2'>
                      <Controller
                        control={control}
                        name='repeatStartDate'
                        render={({ field }) => (
                          <Input
                            {...field}
                            label='Scheduled Start'
                            type='date'
                            value={field.value ?? ''}
                            error={errors.repeatStartDate?.message}
                            showClearIcon
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name='time'
                        render={({ field: { value, onChange } }) => (
                          <TimePicker
                            label='Start Time'
                            value={value ?? ''}
                            onChange={(val) => onChange(val || null)}
                            error={errors.time?.message}
                            showClearIcon
                          />
                        )}
                      />
                    </div>

                    {/* Repeats Section */}
                    <div className='mb-5'>
                      <label className='mb-2 block text-sm font-medium'>
                        Repeats
                      </label>
                      <div className='mb-3 grid grid-cols-[auto_1fr_1fr_1fr] gap-2'>
                        <span className='flex items-start justify-center px-1 pt-2.5'>
                          Every
                        </span>
                        <Input
                          {...register('repeat.interval')}
                          label=''
                          type='number'
                          min={1}
                          max={999}
                          step={1}
                          inputMode='numeric'
                        />
                        <Controller
                          control={control}
                          name='repeat.freq'
                          render={({ field }) => (
                            <Select
                              hasSelectorIcon
                              label=''
                              className='col-span-2'
                              options={['daily', 'weekly', 'monthly'].map(
                                (value) => ({
                                  value,
                                  label: t(`repeatFrequencies.${value}`),
                                })
                              )}
                              value={field.value ? [field.value] : []}
                              onChange={(selected) =>
                                field.onChange(
                                  selected.length ? selected[0] : ''
                                )
                              }
                            />
                          )}
                        />
                      </div>

                      {/* Repeat on (for weekly and monthly) */}
                      {repeatFreq === 'weekly' && (
                        <div className='-mt-1 mb-5'>
                          <label className='mb-2 block text-sm font-medium'>
                            Repeat on
                          </label>
                          <Controller
                            control={control}
                            name='repeat.byday'
                            render={({ field }) => (
                              <div className='flex items-center justify-between gap-0 sm:justify-start sm:gap-1 lg:gap-2'>
                                {DAYS_DISPLAY_ORDER.map((dayName, index) => (
                                  <div
                                    className='rounded-full'
                                    onClick={() => {
                                      const values = [...(field.value ?? [])];
                                      field.onChange(
                                        values.includes(dayName)
                                          ? values.filter(
                                              (val) => val !== dayName
                                            )
                                          : [...values, dayName]
                                      );
                                    }}
                                    key={index}
                                  >
                                    <Avatar
                                      className={classNames(
                                        '!h-10 !w-10 cursor-pointer',
                                        {
                                          '!bg-brand !text-sm text-white':
                                            field.value?.includes(dayName),
                                        }
                                      )}
                                      name={dayName}
                                      size='medium'
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          />
                        </div>
                      )}
                      {repeatFreq === 'monthly' && (
                        <div className='mb-4'>
                          <label className='mb-2 block text-sm font-medium'>
                            Repeat on
                          </label>
                          <Controller
                            control={control}
                            name='repeat.bymonthday'
                            render={({ field }) => (
                              <Select
                                hasSelectorIcon
                                label=''
                                options={monthOptions}
                                value={field.value ? [field.value] : []}
                                onChange={(selected) =>
                                  field.onChange(
                                    selected.length
                                      ? selected[0]
                                      : monthOptions[0].value
                                  )
                                }
                                sortItems={false}
                              />
                            )}
                          />
                        </div>
                      )}

                      {/* Maintain Repeats */}
                      <Controller
                        control={control}
                        name='maintainRepeats'
                        render={({ field }) => {
                          // Destructure to avoid conflicts with our custom handlers
                          const { value, onChange, onBlur, ...fieldProps } =
                            field;

                          const displayValue =
                            value === null ||
                            value === undefined ||
                            Number.isNaN(value)
                              ? ''
                              : String(value);

                          return (
                            <Input
                              {...fieldProps}
                              label='No. of Repeats to always maintain'
                              type='number'
                              value={displayValue}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const numValue = e?.currentTarget?.value;

                                // Ignore if value is undefined or null (likely a blur or other event)
                                if (
                                  numValue === undefined ||
                                  numValue === null
                                ) {
                                  return;
                                }

                                if (numValue === '') {
                                  onChange(null);
                                } else {
                                  const parsed = parseInt(numValue, 10);
                                  const finalValue = Number.isNaN(parsed)
                                    ? null
                                    : parsed;
                                  onChange(finalValue);
                                }
                              }}
                              onBlur={onBlur}
                              error={errors.maintainRepeats?.message}
                            />
                          );
                        }}
                      />
                    </div>

                    {/* NEEDS BACKEND SUPPORT: Exclude days section (same as booking) */}
                    {/*  <div className='mb-5'>
                    <Controller
                      name='excludeDays'
                      control={control}
                      render={({ field }) => (
                        <Switch
                          label='Exclude days'
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    {excludeDays && (
                      <div className='mt-4'>
                        <label className='mb-2 block text-sm font-medium'>
                          Do not schedule on these days
                        </label>
                        <Controller
                          control={control}
                          name='excludeDaysList'
                          render={({ field }) => (
                            <div className='mb-3 flex items-center justify-between gap-0 sm:justify-start sm:gap-1 lg:gap-2'>
                              {DAYS.map((dayName, index) => (
                                <div
                                  className='rounded-full'
                                  onClick={() => {
                                    const values = [...(field.value ?? [])];
                                    field.onChange(
                                      values.includes(dayName)
                                        ? values.filter(
                                            (val) => val !== dayName
                                          )
                                        : [...values, dayName]
                                    );
                                  }}
                                  key={index}
                                >
                                  <Avatar
                                    className={classNames(
                                      '!h-10 cursor-pointer',
                                      {
                                        '!bg-brand text-white':
                                          field.value?.includes(dayName),
                                      }
                                    )}
                                    name={dayName}
                                    size='medium'
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        />
                        <label className='mb-2 block text-sm font-medium'>
                          Schedule instead on the day
                        </label>
                        <div className='mb-3 flex gap-2'>
                          <Controller
                            name='excludeFallback'
                            control={control}
                            render={({ field }) => (
                              <Select
                                hasSelectorIcon
                                label=''
                                options={[
                                  { value: 'after', label: 'After' },
                                  { value: 'before', label: 'Before' },
                                ]}
                                value={field.value ? [field.value] : []}
                                onChange={(selected) =>
                                  field.onChange(
                                    selected.length ? selected[0] : 'after'
                                  )
                                }
                              />
                            )}
                          />
                        </div>
                        <Controller
                          control={control}
                          name='excludeTime'
                          render={({ field: { value, onChange } }) => (
                            <TimePicker
                              label='At time'
                              value={value ?? ''}
                              onChange={(val) => onChange(val || null)}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div> */}

                    {/* NEEDS BACKEND SUPPORT:  Avoid Booking Overlap */}
                    {/*  <div className='mb-5'>
                    <Controller
                      name='avoidBookingOverlap'
                      control={control}
                      render={({ field }) => (
                        <Switch
                          label="Don't overlap with active bookings"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div> */}
                  </>
                )}
              </Accordion.Panel>
            </Accordion.GreyCard>

            {/* Where Panel - Index 2 */}
            <Accordion.GreyCard
              error={!!(errors.sites || errors.matchAttribute)}
            >
              <Accordion.Button
                summary={whereInfo}
                help={
                  errors.sites
                    ? 'Place is required'
                    : errors.matchAttribute
                    ? 'Attribute filter is required'
                    : 'Place'
                }
                error={!!(errors.sites || errors.matchAttribute)}
              >
                Where
              </Accordion.Button>
              <Accordion.Panel>
                <Controller
                  name='whereType'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      title=''
                      options={[
                        { value: AutomationWhereType.All, label: 'All Places' },
                        {
                          value: AutomationWhereType.Match,
                          label: 'Matching Places',
                        },
                        {
                          value: AutomationWhereType.Select,
                          label: 'Selected Places',
                        },
                      ]}
                    />
                  )}
                />

                {whereType === AutomationWhereType.Match && (
                  <Controller
                    name='matchAttribute'
                    control={control}
                    render={({ field }) => (
                      <div
                        className={classNames({
                          'animate-shake': animateFieldId === 'matchAttribute',
                        })}
                      >
                        <FacetedFilterSelect
                          label='Filter by Attribute'
                          options={attributeFilters}
                          value={field.value || []}
                          onChange={field.onChange}
                          error={errors.matchAttribute?.message}
                          required
                          isAttributeFilter={true}
                        />
                      </div>
                    )}
                  />
                )}

                {whereType === AutomationWhereType.Select && (
                  <>
                    <Controller
                      name='sites'
                      control={control}
                      render={({ field, fieldState }) => (
                        <div
                          className={classNames({
                            'animate-shake': animateFieldId === 'sites',
                          })}
                        >
                          <FindAddSite
                            label={t('site', {
                              count: field.value?.length ?? 0,
                            })}
                            error={fieldState.error?.message}
                            value={field.value}
                            onChange={field.onChange}
                            multiple
                            hasSelectAll={true}
                          />
                        </div>
                      )}
                    />
                    {/* TODO BACKEND: includeFuturePlaces field not yet in schema - feature commented out in UI */}
                    {/* { selectedSites?.length === allSites.length &&
                      allSites.length > 0 && (
                        <div className='mt-4'>
                          <Controller
                            name='includeFuturePlaces'
                            control={control}
                            render={({ field }) => (
                              <Switch
                                label='Automatically include future places'
                                tooltip='New places added to the system will automatically be included in this automation'
                                checked={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </div>
                      )} */}
                  </>
                )}
              </Accordion.Panel>
            </Accordion.GreyCard>
            {/* Who Panel - Index 3 */}
            <Accordion.GreyCard error={!!(errors.owner || errors.assignee)}>
              <Accordion.Button
                summary={whoInfo}
                help='Unassigned'
                error={!!(errors.owner || errors.assignee)}
              >
                Who
              </Accordion.Button>
              <Accordion.Panel>
                <Controller
                  name='owner'
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <FindAddContact
                      optionsBoxHeight={
                        isMobile
                          ? 'max-h-[calc(100vh-285px)]'
                          : 'max-h-[calc(100vh-450px)]'
                      }
                      label='Job Owner(s)'
                      error={error?.message}
                      value={
                        value && value.length
                          ? contacts.filter(({ id }) => value.includes(id))
                          : []
                      }
                      onChange={(selected) =>
                        onChange(
                          selected.length ? selected.map(({ id }) => id) : null
                        )
                      }
                      isTeamMember
                      multiple
                      isAbsolute={false}
                    />
                  )}
                />

                <Controller
                  name='assignee'
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <FindAddContact
                      optionsBoxHeight={
                        isMobile
                          ? 'max-h-[calc(100vh-285px)]'
                          : 'max-h-[calc(100vh-450px)]'
                      }
                      label='Assignee(s)'
                      error={error?.message}
                      value={
                        value
                          ? contacts.filter(({ id }) => value.includes(id))
                          : []
                      }
                      onChange={(selected) =>
                        onChange(
                          selected.length ? selected.map(({ id }) => id) : null
                        )
                      }
                      multiple
                      isAbsolute={false}
                    />
                  )}
                />
              </Accordion.Panel>
            </Accordion.GreyCard>
            <Accordion.GreyCard error={!!(errors.jobName || errors.template)}>
              <Accordion.Button
                summary={whatInfo}
                help={
                  errors.jobName
                    ? 'Job Name is required'
                    : errors.template
                    ? 'Template is required'
                    : 'Description'
                }
                error={!!(errors.jobName || errors.template)}
              >
                What
              </Accordion.Button>
              <Accordion.Panel>
                <Input
                  {...register('jobName')}
                  label='Job Name'
                  error={errors.jobName?.message}
                  required
                  className={classNames('mb-4', {
                    'animate-shake': animateFieldId === 'jobName',
                  })}
                />
                {(selectedTemplate || templateOptions.length > 0) && (
                  <Accordion.PanelSection heading={t('translation:template')}>
                    <Controller
                      name='template'
                      control={control}
                      render={({ field }) => (
                        <div
                          className={classNames({
                            'animate-shake': animateFieldId === 'template',
                          })}
                        >
                          <FindAddTemplate
                            label='Selected Template'
                            options={templateOptions}
                            value={field.value}
                            onChange={(id) => field.onChange(id)}
                            onCreate={() => {
                              if (onCreateTemplate) {
                                onCreateTemplate(getValues, () =>
                                  setShowCreateTemplateDialog(true)
                                );
                              } else {
                                setShowCreateTemplateDialog(true);
                              }
                            }}
                            required={true}
                            optionsBoxHeight={
                              isMobile
                                ? 'max-h-[calc(100vh-285px)]'
                                : 'max-h-[calc(100vh-450px)]'
                            }
                            jobTemplates={jobTemplatesFragments}
                          />
                        </div>
                      )}
                    />
                  </Accordion.PanelSection>
                )}

                {/* Asset Selection for Asset-Driven Templates */}
                {templateBehavior === 'asset_rules' && selectedTemplate && (
                  <Accordion.PanelSection>
                    <AssetDrivenJobPreview
                      template={selectedTemplate}
                      allAssets={allAssets}
                      selectedAssetIds={selectedAssetIds || []}
                      onAssetSelectionChange={(assetIds) => {
                        setValue('selectedAssetIds', assetIds);
                      }}
                      defaultLocationId={
                        scopeIds.length === 1 ? scopeIds[0] : undefined
                      }
                      renderAssetsSeparately={false}
                      variant='standard'
                    />
                  </Accordion.PanelSection>
                )}

                <Accordion.PanelSection heading='Summary'>
                  {' '}
                  {/* Inline editor for ALL templates */}
                  {templateData && (
                    <>
                      {/* Template Name */}
                      <Input
                        {...register('templateData.name')}
                        label='Template Name'
                        error={errors.templateData?.name?.message}
                        className='mb-4'
                      />

                      {/* Notes */}
                      <Controller
                        name='templateData.notes'
                        control={control}
                        render={({ field }) => (
                          <div className='space-y-1'>
                            <Label>Notes</Label>
                            <Editor
                              content={field.value}
                              onUpdate={field.onChange}
                              placeholder='Add notes...'
                            />
                          </div>
                        )}
                      />
                    </>
                  )}
                </Accordion.PanelSection>
                {/* Attributes */}
                {templateData && hasSiteAttributes && (
                  <Accordion.PanelSection>
                    <div className='pb-1 text-base font-semibold text-primary'>
                      {t('translation:location_attributes')}
                    </div>
                    <p className='pb-3 text-sm text-secondary'>
                      Information about the job location relevant to this job
                    </p>
                    <Controller
                      name='templateData.includeAttributes'
                      control={control}
                      render={({ field }) => (
                        <AttributeSelect
                          entityType='Site'
                          label='Place attributes to display'
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Accordion.PanelSection>
                )}

                {templateData && hasJobAttributes && (
                  <Accordion.PanelSection>
                    <div className='pb-1 text-base font-semibold text-primary'>
                      Custom Fields
                    </div>
                    <p className='pb-3 text-sm text-secondary'>
                      Metrics you would like collected for this job
                    </p>
                    <Controller
                      name='templateData.jobAttributeIds'
                      control={control}
                      render={({ field }) => (
                        <AttributeSelect
                          entityType='Job'
                          label='Custom Fields'
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Accordion.PanelSection>
                )}
                {/* Tags */}
                {templateData && (
                  <Accordion.PanelSection heading={t('translation:tag_plural')}>
                    <Controller
                      name='templateData.tags'
                      control={control}
                      render={({ field }) => (
                        <TagCombobox
                          entityType={TagEntityType.Job}
                          options={allTags || []}
                          value={
                            allTags?.filter((t) =>
                              field.value?.includes(t.id)
                            ) || []
                          }
                          onChange={(tags) => {
                            field.onChange(
                              tags.length ? tags.map(({ id }) => id) : []
                            );
                          }}
                          hasLabel={false}
                        />
                      )}
                    />
                  </Accordion.PanelSection>
                )}

                {/* Tasks */}
                {templateData && (
                  <Accordion.PanelSection heading='Tasks'>
                    <ManageTasks
                      state={[templateTasks || [], handleTasksUpdate]}
                      onShowTask={handleShowTask}
                      onTaskTypeUpdate={handleTaskTypeUpdate}
                      label=''
                    />
                  </Accordion.PanelSection>
                )}
              </Accordion.Panel>
            </Accordion.GreyCard>
          </Accordion>
        </SideLayout.Body>
        <SideLayout.Foot className='p-4'>
          <Button loading={navigation.state === 'submitting'}>
            {t('submit')}
          </Button>
        </SideLayout.Foot>

        {/* Create Template Dialog */}
        <Dialog
          title='Create New Template'
          show={showCreateTemplateDialog}
          onClose={() => {
            setShowCreateTemplateDialog(false);
            resetTemplateName();
            // Reset external state if dialog was opened externally
            if (externalShowCreateDialog) {
              onCreateDialogShown?.();
            }
          }}
        >
          <form
            onSubmit={handleSubmitTemplateName(async ({ name }) => {
              await handleCreateTemplate(name);
            })}
            className='p-5 text-left'
          >
            <Input
              {...registerTemplateName('name')}
              label='Template Name'
              error={templateNameErrors.name?.message}
              autoFocus
              required
            />

            <div className='mt-5 flex justify-end gap-3'>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium uppercase text-brand-dark transition-all duration-500 hover:text-brand'
                onClick={() => {
                  setShowCreateTemplateDialog(false);
                  resetTemplateName();
                }}
              >
                {t('cancel')}
              </button>
              <Button
                type='submit'
                intent='primary'
                size='small'
                loading={isCreatingTemplate}
              >
                {t('create')}
              </Button>
            </div>
          </form>
        </Dialog>
      </SideLayout>

      {taskSection}
    </div>
  );
}
